import React from 'react';
import { Info, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

interface ImportFailedModalProps {
  open: boolean;
  onClose: () => void;
  invalidUrl?: string | null;
  errorMessage?: string | null;
  errorCode?: string | null;
  onTryAgain?: () => void;
}

const ImportFailedModal: React.FC<ImportFailedModalProps> = ({
  open,
  onClose,
  invalidUrl,
  errorMessage,
  errorCode,
  onTryAgain,
}) => {
  const { t } = useTranslation();

  const getDisplayMessage = (): string => {
    if (errorCode) {
      const translationKey = `import.failed.errors.${errorCode}`;
      const translated = t(translationKey);
      // If the key exists in translations, use it; otherwise fall back to raw errorMessage
      if (translated !== translationKey) {
        return translated;
      }
    }
    return errorMessage || t('import.failed.description');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t('import.failed.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-foreground">{getDisplayMessage()}</p>

          {invalidUrl && (
            <p className="text-sm text-muted-foreground italic break-all">
              {t('import.failed.urlPrefix')}
              {invalidUrl}
            </p>
          )}

          {!errorMessage && !errorCode && (
            <Alert className="bg-blue-50/50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-900/20">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-sm text-muted-foreground">
                {t('import.failed.possibleIssues')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="mt-6">
          {onTryAgain && (
            <Button onClick={onTryAgain} className="rounded-full">
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('import.failed.tryAgain')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFailedModal;
